import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useAppDispatch from "hooks/useAppDispatch";
import { login } from "../../../../redux/features/auth/authSlice";
import { isEmail } from "utils/validation";
import {
  handlePostLoginRedirect,
  isDevOrLocalhostAuth,
} from "../utils/authRedirect";

export default function useLogInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggingIn = useSelector((state) => state.auth.loggingInLoading);

  const [step, setStep] = useState("credentials");
  const [passwordChecked, setPasswordChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [errorPassword, setErrorPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaError, setTwoFaError] = useState("");

  const validateEmail = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized.length > 0 && !isEmail(normalized)) {
        setErrorEmail(t("login.invalidEmail"));
      } else {
        setErrorEmail(false);
      }
    },
    [t]
  );

  const handleLoginSuccess = useCallback(
    (res) => {
      handlePostLoginRedirect(res, navigate);
    },
    [navigate]
  );

  const resolveLoginError = useCallback(
    (error, fallbackKey) => {
      const payload = error?.payload ?? error;
      if (typeof payload === "string") {
        return payload;
      }
      if (payload?.error) {
        return payload.error;
      }
      return t(fallbackKey);
    },
    [t]
  );

  const doLogin = useCallback(async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !password) {
      setErrorPassword(t("login.enterPassAndEmail"));
      return;
    }
    if (normalizedEmail !== email) {
      setEmail(normalizedEmail);
    }

    try {
      const payload = await dispatch(login({ email: normalizedEmail, password }));

      if (payload?.error) {
        setErrorPassword(
          typeof payload.error === "string"
            ? payload.error
            : t("auth.login.emailOrPasswordIncorrect")
        );
        return;
      }

      if (payload?.require_two_factor === true) {
        setStep("twoFactor");
        setTwoFaCode("");
        setTwoFaError("");
        return;
      }

      handleLoginSuccess(payload);
    } catch (error) {
      setErrorPassword(
        resolveLoginError(error, "auth.login.emailOrPasswordIncorrect")
      );
    }
  }, [
    dispatch,
    email,
    password,
    t,
    handleLoginSuccess,
    resolveLoginError,
  ]);

  const submitTwoFactor = useCallback(async () => {
    if (!twoFaCode || twoFaCode.length !== 6) {
      return;
    }

    setTwoFaError("");

    try {
      const payload = await dispatch(
        login({ email: email.trim(), password, code: twoFaCode })
      );

      if (payload?.error) {
        setTwoFaError(payload.error);
        return;
      }

      handleLoginSuccess(payload);
    } catch (error) {
      setTwoFaError(
        resolveLoginError(error, "auth.twoFa.wrongVerificationCode")
      );
    }
  }, [
    dispatch,
    email,
    password,
    twoFaCode,
    handleLoginSuccess,
    resolveLoginError,
  ]);

  const handleBackToCredentials = useCallback(() => {
    setStep("credentials");
    setTwoFaCode("");
    setTwoFaError("");
  }, []);

  const handleTwoFaCodeChange = useCallback((value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
    setTwoFaCode(digitsOnly);
    setTwoFaError("");
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handlePasswordChange = useCallback((value) => {
    setPassword(value);
    setErrorPassword(false);
  }, []);

  return {
    t,
    navigate,
    loggingIn,
    step,
    passwordChecked,
    email,
    errorEmail,
    password,
    errorPassword,
    showPassword,
    twoFaCode,
    twoFaError,
    isDevOrLocalhost: isDevOrLocalhostAuth(),
    setPasswordChecked,
    setEmail,
    validateEmail,
    togglePasswordVisibility,
    handlePasswordChange,
    doLogin,
    submitTwoFactor,
    handleBackToCredentials,
    handleTwoFaCodeChange,
    setErrorPassword,
    setTwoFaError,
  };
}
